import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';  
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';  
import {
  LayoutDashboard,
  Monitor,
  Wallet,
  Banknote,
  Users,
  Settings,
  LogOut,
  Droplet,
  ShieldCheck,
  History,
  CreditCard,
  ClipboardCheck,
  Languages,
  ChevronUp,
  ChevronDown,
  Lock,
  X,
  Loader2,
  TrendingUp,
  Rocket,
  Terminal,
  FileText,
  Siren,
  ClipboardList,
  Briefcase,
  Gift,
  UserCog,
  Menu,
  Smartphone,
  WifiOff
} from 'lucide-react';
import { AdminModalSurface } from './AdminModal';
import { authAPI, applicationAPI } from '../services/api';
import { normalizeAdminLanguage } from '../utils/language';
import { filterMenuGroupsByPermission, hasAdminPermission } from '../utils/adminPermissions';
import { getAdminPasswordErrorCode } from '../utils/adminPasswordPolicy';
import {
  ensureActiveGroupExpanded,
  matchesNavigationPath,
  readCollapsedNavigationGroups,
  toggleCollapsedNavigationGroup,
  writeCollapsedNavigationGroups
} from '../utils/navigationGroupState';
import './Navigation.css';  

export default function Navigation() {  
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuthStore();  
  const navigate = useNavigate();  
  const location = useLocation();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordFeedback, setPasswordFeedback] = useState(null);
  const [settingPassword, setSettingPassword] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopFooterExpanded, setDesktopFooterExpanded] = useState(() => (
    typeof window !== 'undefined'
      && window.localStorage.getItem('admin.navigation.footer.expanded.v1') === 'true'
  ));
  const desktopFooterToggleRef = useRef(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 767px)').matches
  ));
  const baseLang = normalizeAdminLanguage(i18n.resolvedLanguage || i18n.language);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(max-width: 767px)');
    const updateViewport = (event) => setIsMobileViewport(event.matches);
    const updateFromWindow = () => setIsMobileViewport(window.innerWidth <= 767);
    updateFromWindow();
    media.addEventListener('change', updateViewport);
    window.addEventListener('resize', updateFromWindow);
    return () => {
      media.removeEventListener('change', updateViewport);
      window.removeEventListener('resize', updateFromWindow);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        'admin.navigation.footer.expanded.v1',
        desktopFooterExpanded ? 'true' : 'false'
      );
    }
  }, [desktopFooterExpanded]);

  useEffect(() => {
    const closeFooterOnEscape = (event) => {
      if (event.key !== 'Escape' || !desktopFooterExpanded || isMobileViewport) return;
      setDesktopFooterExpanded(false);
      requestAnimationFrame(() => desktopFooterToggleRef.current?.focus());
    };
    document.addEventListener('keydown', closeFooterOnEscape);
    return () => document.removeEventListener('keydown', closeFooterOnEscape);
  }, [desktopFooterExpanded, isMobileViewport]);

  const navCopy = {
    zh: {
      groupOps: '运营监控',
      groupUsers: '用户与角色',
      groupDeviceOps: '设备运维',
      groupFinance: '财务中心',
      configuration: '系统配置',
      groupAdmin: '系统管理',
      dashboard: '控制面板',
      units: '水站管理',
      simCards: 'SIM 卡管理',
      users: '用户与角色',
      adminAccounts: '后台账号',
      stewards: '水站管家',
      rps: '合伙人 PA',
      puskud: '区域合伙人 RP',
      applications: '申请审核',
      finance: '财务管理',
      profitSharing: '收入分配',
      transactions: '交易明细',
      withdrawals: '提现管理',
      bankAccounts: '银行卡与应付',
      cards: '实物水卡',
      deviceAlerts: '设备告警',
      stewardCheckins: '管家巡检',
      stewardChanges: '水管家换人审核',
      profitRules: '收入分配规则',
      firmwareUpload: '上传固件',
      firmwareList: '固件列表',
      upgradeMonitor: '升级监控',
      firmwareUpgrade: '固件升级',
      settings: '系统设置',
      growthSettings: '用户裂变设置',
      audit: '系统审计',
      deviceOfflineAnalysis: '设备断网分析',
      runtimeLogs: '运行日志',
      content: '内容管理',
      language: '语言',
      langChinese: '简体中文',
      langEnglish: 'English',
      langIndonesian: 'Bahasa Indonesia',
      admin: '管理员',
      superadmin: '超级管理员',
      setPassword: '设置密码',
      build: '版本',
      logout: '退出登录',
      expandGroup: '展开',
      collapseGroup: '折叠'
    },
    id: {
      groupOps: 'Operasional',
      groupUsers: 'Pengguna & Peran',
      groupDeviceOps: 'Operasional Perangkat',
      groupFinance: 'Pusat Keuangan',
      configuration: 'Konfigurasi',
      groupAdmin: 'Administrasi',
      dashboard: 'Dasbor',
      units: 'Manajemen Stasiun',
      simCards: 'Manajemen Kartu SIM',
      users: 'Pengguna & Peran',
      adminAccounts: 'Akun Admin',
      stewards: 'Pengelola',
      rps: 'Mitra PA',
      puskud: 'Mitra Regional RP',
      applications: 'Tinjauan Aplikasi',
      finance: 'Keuangan',
      profitSharing: 'Distribusi Pendapatan',
      transactions: 'Transaksi',
      withdrawals: 'Penarikan',
      bankAccounts: 'Rekening & Hutang',
      cards: 'Kartu Air',
      deviceAlerts: 'Peringatan Perangkat',
      stewardCheckins: 'Check-in Pengelola',
      stewardChanges: 'Pergantian Steward',
      profitRules: 'Aturan Distribusi Pendapatan',
      firmwareUpload: 'Unggah Firmware',
      firmwareList: 'Daftar Firmware',
      upgradeMonitor: 'Monitor Upgrade',
      firmwareUpgrade: 'Upgrade Firmware',
      settings: 'Pengaturan Sistem',
      growthSettings: 'Program Pertumbuhan',
      audit: 'Audit Sistem',
      deviceOfflineAnalysis: 'Analisis Perangkat Offline',
      runtimeLogs: 'Log Runtime',
      content: 'Manajemen Konten',
      language: 'Bahasa',
      langChinese: '简体中文',
      langEnglish: 'English',
      langIndonesian: 'Bahasa Indonesia',
      admin: 'Admin',
      superadmin: 'Super Admin',
      setPassword: 'Atur Kata Sandi',
      build: 'Versi',
      logout: 'Keluar',
      expandGroup: 'Buka',
      collapseGroup: 'Tutup'
    },
    en: {
      groupOps: 'Operations',
      groupUsers: 'Users & Roles',
      groupDeviceOps: 'Device Operations',
      groupFinance: 'Finance Center',
      configuration: 'Configuration',
      groupAdmin: 'Administration',
      dashboard: 'Dashboard',
      units: 'Unit Management',
      simCards: 'SIM Card Management',
      users: 'Users & Roles',
      adminAccounts: 'Admin Accounts',
      stewards: 'Stewards',
      rps: 'Partner PA',
      puskud: 'Regional Partner RP',
      applications: 'Applications',
      finance: 'Finance',
      profitSharing: 'Income Distribution',
      transactions: 'Transactions',
      withdrawals: 'Withdrawals',
      bankAccounts: 'Bank Accounts & Payables',
      cards: 'Water Cards',
      deviceAlerts: 'Device Alerts',
      stewardCheckins: 'Steward Check-ins',
      stewardChanges: 'Steward Changes',
      profitRules: 'Income Distribution Rules',
      firmwareUpload: 'Upload Firmware',
      firmwareList: 'Firmware List',
      upgradeMonitor: 'Upgrade Monitor',
      firmwareUpgrade: 'Firmware Upgrade',
      settings: 'System Settings',
      growthSettings: 'User Growth Settings',
      audit: 'System Audit',
      deviceOfflineAnalysis: 'Device Offline Analysis',
      runtimeLogs: 'Runtime Logs',
      content: 'Content Management',
      language: 'Language',
      langChinese: 'Simplified Chinese',
      langEnglish: 'English',
      langIndonesian: 'Indonesian',
      admin: 'Admin',
      superadmin: 'Super Admin',
      setPassword: 'Set Password',
      build: 'Build',
      logout: 'Logout',
      expandGroup: 'Expand',
      collapseGroup: 'Collapse'
    }
  }[baseLang] || {};

  // 鑾峰彇寰呭鏍哥敵璇锋暟閲?
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (hasAdminPermission(user, 'review_applications')) {
        try {
          const res = await applicationAPI.getPendingCount();
          setPendingCount(res.data?.data?.count ?? res.data?.count ?? 0);
        } catch (error) {
          console.error('Failed to fetch pending count:', error);
        }
      }
    };

    fetchPendingCount();
    // 姣?0绉掑埛鏂颁竴娆?
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const languages = [
    { code: 'id', label: navCopy.langIndonesian, flag: 'ID' },
    { code: 'en', label: navCopy.langEnglish, flag: 'EN' },
    { code: 'zh', label: navCopy.langChinese, flag: 'ZH' },
  ];

  const changeLanguage = (code) => {
    const nextLanguage = normalizeAdminLanguage(code);
    i18n.changeLanguage(nextLanguage);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = nextLanguage;
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('i18nextLng', nextLanguage);
    }
    setShowLangMenu(false);
  };

  const isActiveRoute = (path) => matchesNavigationPath(location.pathname, path);

  const handleLogout = () => {  
    logout();  
    navigate('/login');  
  };  

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setPasswordFeedback(null);
    if (!oldPassword) {
      setPasswordFeedback({ type: 'error', message: t('nav.oldPasswordRequired') });
      return;
    }
    const policyError = getAdminPasswordErrorCode(newPassword);
    if (policyError) {
      const policyKeys = {
        length: 'nav.passwordRuleLength',
        lettersAndNumbers: 'nav.passwordRuleLettersAndNumbers',
        spaces: 'nav.passwordRuleSpaces',
        blocked: 'nav.passwordRuleBlocked'
      };
      setPasswordFeedback({ type: 'error', message: t(policyKeys[policyError]) });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordFeedback({ type: 'error', message: t('nav.passwordMismatch') });
      return;
    }

    setSettingPassword(true);
    try {
      const res = await authAPI.changeAdminPassword(oldPassword, newPassword);
      if (res.data.success) {
        setPasswordFeedback({ type: 'success', message: t('nav.passwordSetSuccess') });
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      const isIncorrectOldPassword = err?.response?.status === 400
        && err?.response?.data?.message === 'Old password is incorrect';
      setOldPassword('');
      setPasswordFeedback({
        type: 'error',
        message: t(isIncorrectOldPassword ? 'nav.oldPasswordIncorrect' : 'nav.passwordSetFailed')
      });
    } finally {
      setSettingPassword(false);
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordFeedback(null);
  };

  const menuGroups = [
    {
      key: 'operations',
      title: navCopy.groupOps,
      items: [
        { path: '/', label: navCopy.dashboard, icon: LayoutDashboard, permission: 'view_dashboard' },
        { path: '/applications', label: navCopy.applications, icon: ClipboardCheck, permission: 'review_applications' },
      ]
    },
    {
      key: 'users-and-roles',
      title: navCopy.groupUsers,
      items: [
        { path: '/users', label: navCopy.users, icon: Users, permission: 'view_users_roles' },
        { path: '/stewards', label: navCopy.stewards, icon: Users, permission: 'view_partner_network' },
        { path: '/pa', label: navCopy.rps, icon: Briefcase, permission: 'view_partner_network' },
        { path: '/rp', label: navCopy.puskud, icon: ShieldCheck, permission: 'view_partner_network' },
      ]
    },
    {
      key: 'device-operations',
      title: navCopy.groupDeviceOps,
      items: [
        { path: '/units', label: navCopy.units, icon: Monitor, permission: 'view_units' },
        { path: '/sim-cards', label: navCopy.simCards, icon: Smartphone, permission: 'view_sim_cards' },
        { path: '/device-alerts', label: navCopy.deviceAlerts, icon: Siren, permission: 'view_device_alerts' },
        { path: '/steward-checkins', label: navCopy.stewardCheckins, icon: ClipboardList, permission: 'review_steward_checkins' },
        { path: '/steward-changes', label: navCopy.stewardChanges, icon: UserCog, permission: 'review_steward_changes' },
        { path: '/firmware/upgrade', label: navCopy.firmwareUpgrade || navCopy.upgradeMonitor, icon: Rocket, permission: 'manage_firmware' },
      ]
    },
    {
      key: 'finance',
      title: navCopy.groupFinance,
      items: [
        { path: '/finance', label: navCopy.finance, icon: Wallet, permission: 'view_finance' },
        { path: '/profit-sharing-finance', label: navCopy.profitSharing, icon: TrendingUp, permission: 'view_finance' },
        { path: '/bank-accounts', label: navCopy.bankAccounts, icon: Banknote, permission: 'view_bank_accounts' },
        { path: '/transactions', label: navCopy.transactions, icon: History, permission: 'view_finance' },
        { path: '/cards', label: navCopy.cards, icon: CreditCard, permission: 'view_physical_cards' },
        { path: '/profit-rules', label: navCopy.profitRules, icon: TrendingUp, permission: 'manage_profit_rules' },
      ]
    },
    {
      key: 'administration',
      title: navCopy.groupAdmin,
      items: [
        { path: '/settings', label: navCopy.settings, icon: Settings, permission: 'manage_business_settings' },
        { path: '/settings/growth', label: navCopy.growthSettings, icon: Gift, permission: 'manage_business_settings' },
        { path: '/admin-accounts', label: navCopy.adminAccounts, icon: UserCog, permission: 'manage_admin_accounts' },
        { path: '/audit', label: navCopy.audit, icon: ShieldCheck, permission: 'view_audit_logs' },
        { path: '/device-offline-analysis', label: navCopy.deviceOfflineAnalysis, icon: WifiOff, permission: 'view_device_offline_analysis' },
        { path: '/runtime-logs', label: navCopy.runtimeLogs, icon: Terminal, permission: 'view_runtime_logs' },
        { path: '/content', label: navCopy.content, icon: FileText, permission: 'manage_content' },
      ]
    }
  ];
  const visibleMenuGroups = filterMenuGroupsByPermission(menuGroups, user);
  const visibleGroupKeys = visibleMenuGroups.map(({ key }) => key);
  const [collapsedMenuGroups, setCollapsedMenuGroups] = useState(() => {
    const storage = typeof window !== 'undefined' ? window.localStorage : null;
    const persisted = readCollapsedNavigationGroups(storage, visibleGroupKeys);
    const initial = ensureActiveGroupExpanded(persisted, visibleMenuGroups, location.pathname);
    if (initial.length !== persisted.length) writeCollapsedNavigationGroups(storage, initial);
    return initial;
  });
  const activeGroupKey = visibleMenuGroups.find((group) => (
    group.items.some((item) => isActiveRoute(item.path))
  ))?.key;

  useEffect(() => {
    if (!activeGroupKey) return;
    setCollapsedMenuGroups((current) => {
      const next = current.filter((key) => key !== activeGroupKey);
      if (next.length === current.length) return current;
      writeCollapsedNavigationGroups(
        typeof window !== 'undefined' ? window.localStorage : null,
        next
      );
      return next;
    });
  }, [activeGroupKey]);

  const toggleMenuGroup = (groupKey) => {
    setCollapsedMenuGroups((current) => {
      const next = toggleCollapsedNavigationGroup(current, groupKey);
      writeCollapsedNavigationGroups(
        typeof window !== 'undefined' ? window.localStorage : null,
        next
      );
      return next;
    });
  };

  return (  
    <aside
      className={`sidebar flex shrink-0 flex-col overflow-hidden border-b border-white/5 bg-slate-900 text-white md:border-b-0 md:border-r ${mobileMenuOpen ? 'sidebar-mobile-open' : 'sidebar-mobile-collapsed'}`}
      style={{
        width: isMobileViewport ? '100%' : '16rem',
        minHeight: isMobileViewport ? 0 : '100vh',
        height: isMobileViewport ? (mobileMenuOpen ? '100vh' : '80px') : '100vh',
        maxHeight: isMobileViewport ? (mobileMenuOpen ? '100vh' : '80px') : '100vh',
      }}
    >
      <div className="mb-0 flex items-center justify-between p-4 md:mb-4 md:p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-400 rounded-xl flex items-center justify-center text-slate-900 shadow-lg shadow-cyan-400/20">
            <Droplet size={24} fill="currentColor" />
          </div>
          <span className="nav-brand-title font-bold tracking-tight text-white font-mono">AirKOP</span>
        </div>
        <button
          type="button"
          onClick={() => setMobileMenuOpen(open => !open)}
          className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-slate-800 text-white md:hidden"
          aria-label={mobileMenuOpen ? '关闭导航菜单' : '打开导航菜单'}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <nav className={`flex-1 space-y-8 overflow-y-auto px-4 py-4 custom-scrollbar md:block ${mobileMenuOpen ? 'block' : 'hidden'}`}>
        {visibleMenuGroups.map((group) => {
          const isCollapsed = collapsedMenuGroups.includes(group.key);
          return (
          <div key={group.key} className="space-y-2">
            <h3 className="nav-group-title font-black">
              <button
                type="button"
                onClick={() => toggleMenuGroup(group.key)}
                className="nav-group-toggle flex w-full items-center justify-between rounded-lg px-4 py-2 text-left uppercase text-slate-400 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                aria-expanded={!isCollapsed}
                aria-controls={`nav-group-${group.key}`}
                aria-label={`${isCollapsed ? navCopy.expandGroup : navCopy.collapseGroup} ${group.title}`}
              >
                <span>{group.title}</span>
                <ChevronDown
                  size={16}
                  aria-hidden="true"
                  className={`shrink-0 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`}
                />
              </button>
            </h3>
            <div
              id={`nav-group-${group.key}`}
              className="space-y-1"
              hidden={isCollapsed}
            >
              {group.items.map((item) => (
                <Link 
                  key={item.path}
                  to={item.path} 
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group relative ${
                    isActiveRoute(item.path)
                      ? 'bg-cyan-400 text-slate-900 font-bold shadow-lg shadow-cyan-400/10' 
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <item.icon size={18} className={isActiveRoute(item.path) ? 'text-slate-900' : 'group-hover:text-cyan-400 transition-colors'} />
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.path === '/applications' && pendingCount > 0 && (
                    <span className="absolute top-2 right-2 w-5 h-5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                      {pendingCount > 9 ? '9+' : pendingCount}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>
          );
        })}
      </nav>

      <div className={`mt-auto border-t border-white/5 bg-slate-900/50 p-4 md:block ${mobileMenuOpen ? 'block' : 'hidden'}`}>
        <button
          ref={desktopFooterToggleRef}
          type="button"
          onClick={() => setDesktopFooterExpanded((expanded) => !expanded)}
          className="mb-3 hidden w-full items-center gap-3 rounded-xl border border-white/10 bg-slate-800/70 px-3 py-2.5 text-left transition-colors hover:border-cyan-400/40 hover:bg-slate-800 md:flex"
          aria-expanded={desktopFooterExpanded}
          aria-controls="desktop-navigation-account-tools"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-cyan-400/20 text-xs font-bold text-cyan-400">
            {user?.name?.substring(0, 2).toUpperCase() || 'AD'}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-bold text-white">{user?.name || navCopy.admin}</span>
            <span className="block truncate text-[10px] text-slate-500">{languages.find((language) => language.code === baseLang)?.label || navCopy.language}</span>
          </span>
          <ChevronUp size={16} className={`shrink-0 text-slate-400 transition-transform ${desktopFooterExpanded ? '' : 'rotate-180'}`} />
        </button>
        <div
          id="desktop-navigation-account-tools"
          className={`block ${desktopFooterExpanded ? 'md:block' : 'md:hidden'}`}
        >
        {/* Language Switcher */}
        <div className="relative mb-4">
          <button 
            onClick={() => setShowLangMenu(!showLangMenu)}
            className="w-full flex items-center justify-between px-4 py-2 bg-slate-800/50 border border-white/5 rounded-xl text-xs text-slate-400 hover:text-white transition-all group"
          >
            <div className="flex items-center gap-2">
              <Languages size={14} className="group-hover:text-cyan-400 transition-colors" />
              <span>{languages.find(l => l.code === baseLang)?.label || navCopy.language}</span>
            </div>
            <ChevronUp size={14} className={`transition-transform duration-300 ${showLangMenu ? 'rotate-180' : ''}`} />
          </button>
          
          {showLangMenu && (
            <div className="absolute bottom-full left-0 w-full mb-2 bg-slate-800 border border-white/10 rounded-xl overflow-hidden shadow-2xl z-50 animate-in fade-in slide-in-from-bottom-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-xs transition-all hover:bg-white/5 ${
                    baseLang === lang.code ? 'text-cyan-400 font-bold bg-cyan-400/5' : 'text-slate-400'
                  }`}
                >
                  <span className="text-base">{lang.flag}</span>
                  {lang.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mb-4 rounded-2xl border border-white/5 bg-slate-800/50 p-3">
          <button 
            onClick={() => {
              setPasswordFeedback(null);
              setShowPasswordModal(true);
            }}
            className="flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-bold text-slate-300 transition-colors hover:bg-white/5 hover:text-cyan-400"
          >
            <Lock size={14} />
            {navCopy.setPassword}
          </button>
        </div>

        {/* Password Modal */}
        {showPasswordModal && (
          <AdminModalSurface onClose={closePasswordModal} ariaLabelledBy="nav-password-title">
            <div className="bg-slate-900 border border-white/10 w-full max-w-sm rounded-[2rem] p-8 shadow-2xl animate-in zoom-in-95 duration-200">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 id="nav-password-title" className="text-xl font-bold text-white">{t('nav.setPasswordTitle')}</h3>
                  <p className="text-xs text-slate-500 mt-1">{t('nav.setPasswordSubtitle')}</p>
                </div>
                <button type="button" onClick={closePasswordModal} aria-label={t('nav.closePasswordDialog')} className="text-slate-500 hover:text-white transition-colors p-2">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSetPassword} noValidate className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="nav-old-password" className="text-[10px] uppercase font-black tracking-widest text-slate-500 ml-1">{t('nav.oldPassword')}</label>
                    <input
                      id="nav-old-password"
                      type="password"
                      value={oldPassword}
                      onChange={(e) => {
                        setOldPassword(e.target.value);
                        setPasswordFeedback(null);
                      }}
                      placeholder={t('nav.oldPasswordPlaceholder')}
                      autoComplete="current-password"
                      required
                      className="w-full bg-slate-800 border border-white/5 rounded-2xl py-4 px-6 text-white outline-none focus:border-cyan-400 transition-all placeholder:text-slate-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="nav-new-password" className="text-[10px] uppercase font-black tracking-widest text-slate-500 ml-1">{t('nav.newPassword')}</label>
                    <input
                      id="nav-new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setPasswordFeedback(null);
                      }}
                      placeholder={t('nav.minChars')}
                      autoComplete="new-password"
                      minLength={8}
                      disabled={!oldPassword}
                      required
                      className="w-full bg-slate-800 border border-white/5 rounded-2xl py-4 px-6 text-white outline-none focus:border-cyan-400 transition-all placeholder:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="nav-confirm-password" className="text-[10px] uppercase font-black tracking-widest text-slate-500 ml-1">{t('nav.confirmPassword')}</label>
                    <input
                      id="nav-confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordFeedback(null);
                      }}
                      placeholder={t('nav.repeatPassword')}
                      autoComplete="new-password"
                      minLength={8}
                      disabled={!oldPassword}
                      required
                      className="w-full bg-slate-800 border border-white/5 rounded-2xl py-4 px-6 text-white outline-none focus:border-cyan-400 transition-all placeholder:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </div>

                {passwordFeedback && (
                  <div
                    role={passwordFeedback.type === 'error' ? 'alert' : 'status'}
                    className={`rounded-xl border px-4 py-3 text-sm ${passwordFeedback.type === 'error' ? 'border-rose-400/30 bg-rose-400/10 text-rose-300' : 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300'}`}
                  >
                    {passwordFeedback.message}
                  </div>
                )}

                <button 
                  type="submit"
                  disabled={settingPassword || !oldPassword}
                  className="w-full bg-cyan-400 text-slate-900 font-black py-4 rounded-2xl shadow-lg shadow-cyan-400/20 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-95 transition-all disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {settingPassword ? <Loader2 size={20} className="animate-spin" /> : t('nav.savePassword')}
                </button>
              </form>
            </div>
          </AdminModalSurface>
        )}
        <div className="px-4 py-2 mb-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
          <p className="text-[9px] text-emerald-400 font-mono uppercase tracking-wider">{navCopy.build} v1.2.0 - 2026-01-16</p>
        </div>
        <button 
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 text-rose-400 hover:text-rose-300 hover:bg-rose-400/5 rounded-xl transition-all w-full group"
        >
          <LogOut size={18} className="group-hover:translate-x-1 transition-transform" />
          <span className="text-sm font-bold">{navCopy.logout}</span>
        </button>
        </div>
      </div>
    </aside>  
  );  
}  

